import { forkJoin, Observable, of } from 'rxjs'
import { debounceTime, filter, groupBy, map, mergeMap, pluck, switchMap } from 'rxjs/operators'
import { tag } from 'rxjs-spy/operators/tag'
import { ENDPOINTS, SUSPENSE_LISTS_UNPAGED, SUSPENSE_REFRESH_DELAY_MS } from './constants'
import { ENDPOINTS as PARTY_ENDPOINTS } from '../party/constants'
import { ReportingEvent, events } from './state'
import { events as walletEvents, WalletEvent } from '../wallet/state'
import { downloadFileFromArrayBuffer, downloadFileFromBase64 } from 'utils/operators'
import { agencyApiSelectors } from '../agency'
import { userApiSelectors } from '../user'
import { walletApiSelectors } from '../wallet'
import { Epic } from 'redux-observable'
import { isOfType } from 'typesafe-actions'
import { EpicDependencies, RootState } from 'src/state/types'
import {
  TBase64PdfResponse,
  TBase64XlsxResponse,
  TQuickSearchSuccess,
  TReceiptReportResponse,
  TSuspenseAccountStats,
  TSuspenseListResponse,
  TSuspenseReferenceLookup,
} from './types'
import * as transformers from './transformers'
import * as selectors from './selectors'
import { multiUserOperators } from '../../multiUser/state'
import { downloadBase64 } from 'utils/fileUtils'
import { format } from 'date-fns'

type ReportingEpic = Epic<ReportingEvent, ReportingEvent, RootState, EpicDependencies>

export const fetchAccountsReceivable: ReportingEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.accountsReceivable_request.type)),
    mergeMap(action => {
      const currentAgencyId = userApiSelectors.getCurrentAgencyId(state$.value)
      const currentDate = new Date()
      const tradingOrEntity = agencyApiSelectors.getAgencyTradingOrCompanyName(state$.value)(currentAgencyId)
      const payload = action.payload
      const { filters } = payload
      return post(
        ENDPOINTS.ACCOUNTS_RECEIVABLE,
        state$,
        {
          filters: transformers.transformAccountsReceivable(filters),
        },
        null,
        {},
        { responseType: 'arraybuffer' },
      ).pipe(
        pluck('response'),
        downloadFileFromArrayBuffer(
          'application/ms-excel',
          `Accounts Receivable_${tradingOrEntity}_${format(currentDate, 'yyyy-MM-dd')}.xlsx`,
        ),
        map(events.accountsReceivable_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/fetchAccountsReceivable'),
  )

export const fetchCommissionSplit: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.commissionSplit_request.type)),
    mergeMap(action => {
      const payload = action.payload
      const { start = '', end = '', dataType } = payload
      const currentAgencyId = userApiSelectors.getCurrentAgencyId(state$.value)
      const tradingOrEntity = agencyApiSelectors.getAgencyTradingOrCompanyName(state$.value)(currentAgencyId)
      const preferredAsset = walletApiSelectors.getGlobalBalanceAsset(state$.value)(currentAgencyId)
      const requestType = dataType === 'excel' ? 'xlsx' : dataType
      const applicationType =
        dataType === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : dataType
      return get(`${ENDPOINTS.COMMISSION_SPLIT}/${requestType}`, state$, {
        start_date: payload.start,
        end_date: payload.end,
        // `null` until the agency's balance is loaded; `.name` on it threw. A `false`
        // (no wallet) already sent no asset, so a miss now does the same.
        asset: preferredAsset ? preferredAsset.name : undefined,
      }).pipe(
        pluck('response'),
        map((res: TBase64XlsxResponse) => res.xlsxBase64),
        downloadFileFromBase64(applicationType, `Agency Commission Report_${tradingOrEntity}_${start}_${end}`),
        map(events.commissionSplit_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/fetchCommissionSplit'),
  )

export const agencyCommission: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.agencyCommission_request.type)),
    mergeMap(action => {
      const payload = action.payload
      const { start = '', end = '' } = payload
      const currentAgencyId = userApiSelectors.getCurrentAgencyId(state$.value)
      const tradingOrEntity = agencyApiSelectors.getAgencyTradingOrCompanyName(state$.value)(currentAgencyId)
      return get(
        ENDPOINTS.AGENCY_COMMISSION,
        state$,
        {
          start_date: payload.start,
          end_date: payload.end,
        },
        { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
        { responseType: 'arraybuffer' },
      ).pipe(
        pluck('response'),
        downloadFileFromArrayBuffer(
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          `Agency Commission_${tradingOrEntity}_${format(new Date(start), 'yyyy-MM-dd')}_${format(
            new Date(end),
            'yyyy-MM-dd',
          )}.xlsx`,
        ),
        map(events.agencyCommission_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/agencyCommission'),
  )

export const agentCommission: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.agentCommission_request.type)),
    mergeMap(action => {
      const payload = action.payload
      const { start = '', end = '' } = payload
      const currentAgencyId = userApiSelectors.getCurrentAgencyId(state$.value)
      const tradingOrEntity = agencyApiSelectors.getAgencyTradingOrCompanyName(state$.value)(currentAgencyId)
      return get(
        ENDPOINTS.AGENT_COMMISSION,
        state$,
        {
          start_date: payload.start,
          end_date: payload.end,
        },
        { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
        { responseType: 'arraybuffer' },
      ).pipe(
        pluck('response'),
        downloadFileFromArrayBuffer(
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          `Agent Commission_${tradingOrEntity}_${format(new Date(start), 'yyyy-MM-dd')}_${format(
            new Date(end),
            'yyyy-MM-dd',
          )}.xlsx`,
        ),
        map(events.agentCommission_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/agentCommission'),
  )

export const rentRoll: ReportingEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.rentRoll_request.type)),
    mergeMap(action => {
      const payload = action.payload
      const currentDate = new Date()
      const currentAgencyId = userApiSelectors.getCurrentAgencyId(state$.value)
      const tradingOrEntity = agencyApiSelectors.getAgencyTradingOrCompanyName(state$.value)(currentAgencyId)
      return post(
        `${ENDPOINTS.RENT_ROLL}`,
        state$,
        transformers.transformRentRoll(payload),
        null,
        {},
        { responseType: 'arraybuffer' },
      ).pipe(
        pluck('response'),
        downloadFileFromArrayBuffer(
          'application/ms-excel',
          `Rent Roll_${tradingOrEntity}_${format(currentDate, 'yyyy-MM-dd')}.xlsx`,
        ),
        map(events.rentRoll_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/rentRoll'),
  )

export const cashFlow: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.cashFlow_request.type)),
    mergeMap(action => {
      const payload = action.payload
      const { start = '', end = '' } = payload
      const currentAgencyId = userApiSelectors.getCurrentAgencyId(state$.value)
      const tradingOrEntity = agencyApiSelectors.getAgencyTradingOrCompanyName(state$.value)(currentAgencyId)
      return get(
        ENDPOINTS.CASH_FLOW,
        state$,
        { start: start, finish: end },
        { 'Content-Type': 'application/ms-excel' },
        { responseType: 'arraybuffer' },
      ).pipe(
        pluck('response'),
        downloadFileFromArrayBuffer('application/ms-excel', `Cash Flow_${tradingOrEntity}_${start}_${end}.xlsx`),
        map(events.cashFlow_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/cashFlow'),
  )

export const deposits: ReportingEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.deposits_request.type)),
    mergeMap(action => {
      const payload = action.payload
      const currentDate = new Date()
      const currentAgencyId = userApiSelectors.getCurrentAgencyId(state$.value)
      const tradingOrEntity = agencyApiSelectors.getAgencyTradingOrCompanyName(state$.value)(currentAgencyId)
      return post(
        `${ENDPOINTS.DEPOSITS}`,
        state$,
        transformers.transformDeposits(payload),
        null,
        {},
        { responseType: 'arraybuffer' },
      ).pipe(
        pluck('response'),
        downloadFileFromArrayBuffer(
          'application/ms-excel',
          `Deposit Report_${tradingOrEntity}_${format(currentDate, 'yyyy-MM-dd')}.xlsx`,
        ),
        map(events.deposits_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/deposits'),
  )

export const depositSummaryStats: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.depositSummaryStats_request.type)),
    mergeMap(action => {
      return get(ENDPOINTS.DASHBOARD_SUMMARY_STATS, state$, {}).pipe(
        pluck('response'),
        map(events.depositSummaryStats_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/depositSummaryStats'),
  )

export const tenancySchedule: ReportingEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.tenancySchedule_request.type)),
    mergeMap(action => {
      const payload = action.payload
      const { filters } = payload
      const currentAgencyId = userApiSelectors.getCurrentAgencyId(state$.value)
      const tradingOrEntity = agencyApiSelectors.getAgencyTradingOrCompanyName(state$.value)(currentAgencyId)
      const currentDate = new Date()
      return post(
        ENDPOINTS.TENANCY_SCHEDULE,
        state$,
        {
          filters: transformers.transformTenancySchedule(filters),
        },
        null,
        {},
        { responseType: 'arraybuffer' },
      ).pipe(
        pluck('response'),
        downloadFileFromArrayBuffer(
          'application/ms-excel',
          `Tenancy Schedule_${tradingOrEntity}_${format(currentDate, 'yyyy-MM-dd')}.xlsx`,
        ),
        map(events.tenancySchedule_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/tenancySchedule'),
  )

export const agencyGroupCommissionSplit: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.agencyGroupCommissionSplit_request.type)),
    mergeMap(action => {
      const payload = action.payload
      const { start = '', end = '', dataType } = payload
      const currentAgencyId = userApiSelectors.getCurrentAgencyId(state$.value)
      const tradingOrEntity = agencyApiSelectors.getAgencyTradingOrCompanyName(state$.value)(currentAgencyId)
      return get(
        /** @todo the API only supports Rawson at the moment. Temporarily hard coding the agency group ID */
        `${ENDPOINTS.AGENCY_GROUP_COMMISSION_SPLIT}/${dataType}`,
        state$,
        { id: '754cceb6-f295-4d05-85c8-9d7ca4edeb1b', start_date: start, end_date: end },
        { 'Content-Type': 'application/ms-excel' },
        { responseType: 'arraybuffer' },
      ).pipe(
        pluck('response'),
        downloadFileFromArrayBuffer(
          'application/ms-excel',
          `Agency Group Commission Split_${tradingOrEntity}_${start}_${end}.xlsx`,
        ),
        map(events.agencyGroupCommissionSplit_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/agencyGroupCommissionSplit'),
  )

export const billingReport: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.billingReport_request.type)),
    mergeMap(action => {
      const payload = action.payload
      const { start = '', end = '', dataType } = payload
      const currentAgencyId = userApiSelectors.getCurrentAgencyId(state$.value)
      const tradingOrEntity = agencyApiSelectors.getAgencyTradingOrCompanyName(state$.value)(currentAgencyId)
      return get(
        `${ENDPOINTS.BILLING}/${dataType}`,
        state$,
        { start_date: start, end_date: end },
        { 'Content-Type': 'application/ms-excel' },
        { responseType: 'arraybuffer' },
      ).pipe(
        pluck('response'),
        downloadFileFromArrayBuffer('application/ms-excel', `Billing Report_${tradingOrEntity}_${start}_${end}.xlsx`),
        map(events.billingReport_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/billingReport'),
  )

export const tpnReport: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.tpnReport_request.type)),
    mergeMap(action => {
      const currentAgencyId = userApiSelectors.getCurrentAgencyId(state$.value)
      const tradingOrEntity = agencyApiSelectors.getAgencyTradingOrCompanyName(state$.value)(currentAgencyId)
      return get(
        ENDPOINTS.TPN_REPORT,
        state$,
        { start: action.payload.dateParams.start, finish: action.payload.dateParams.end },
        { 'Content-Type': 'text/csv' },
        { responseType: 'arraybuffer' },
      ).pipe(
        pluck('response'),
        downloadFileFromArrayBuffer(
          'text/csv',
          `TPN Report_${tradingOrEntity}_${action.payload.dateParams.start}_${action.payload.dateParams.end}.csv`,
        ),
        map(events.tpnReport_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/tpnReport'),
  )

export const cashDepositStatement: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.cashDepositStatement_request.type)),
    mergeMap(action => {
      const currentAgencyId = userApiSelectors.getCurrentAgencyId(state$.value)
      const tradingOrEntity = agencyApiSelectors.getAgencyTradingOrCompanyName(state$.value)(currentAgencyId)
      const dataType = action.payload.dataType === 'application/pdf' ? 'pdf' : 'csv'
      return get(ENDPOINTS.DEPOSIT_FEE_STATEMENT + '/' + dataType, state$, {
        start_date: action.payload.start,
        end_date: action.payload.end,
      }).pipe(
        pluck('response'),
        map((res: TReceiptReportResponse) => {
          downloadBase64(
            action.payload.dataType === 'application/pdf' ? res.pdfBase64 : res.csvBase64,
            action.payload.dataType,
            `Cash Deposit Statement_${tradingOrEntity}_${action.payload.start}_${action.payload.end}.${
              action.payload.dataType === 'application/pdf' ? 'pdf' : 'csv'
            }`,
          )
          return events.cashDepositStatement_success({})
        }),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/cashDepositStatementCsv'),
  )

export const agencyPerformanceSummaryChart: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.agencyPerformanceSummaryChart_request.type)),
    multiUserOperators.filterCurrentAgencyId(state$),
    mergeMap(action => {
      return get(ENDPOINTS.AGENCY_SUMMARY_CHART, state$, action.payload).pipe(
        pluck('response'),
        map(res => events.agencyPerformanceSummaryChart_success(transformers.transformAgencyPerformanceSummary(res))),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/agencyPerformanceSummaryChart'),
  )

export const fetchAgentCommissionReport: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.agentCommissionReport_request.type)),
    mergeMap(action => {
      const payload = action.payload
      const { start = '', end = '', dataType } = payload
      const currentAgencyId = userApiSelectors.getCurrentAgencyId(state$.value)
      const tradingOrEntity = agencyApiSelectors.getAgencyTradingOrCompanyName(state$.value)(currentAgencyId)
      const preferredAsset = walletApiSelectors.getGlobalBalanceAsset(state$.value)(currentAgencyId)
      const requestType = dataType === 'excel' ? 'xlsx' : dataType
      const applicationType =
        dataType === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : dataType
      return get(`${ENDPOINTS.AGENT_COMMISSION_REPORT}/${requestType}`, state$, {
        start_date: payload.start,
        end_date: payload.end,
        // `null` until the agency's balance is loaded; `.name` on it threw. A `false`
        // (no wallet) already sent no asset, so a miss now does the same.
        asset: preferredAsset ? preferredAsset.name : undefined,
      }).pipe(
        pluck('response'),
        map((res: TBase64XlsxResponse) => res.xlsxBase64),
        downloadFileFromBase64(applicationType, `Agent Commission Report_${tradingOrEntity}_${start}_${end}`),
        map(events.agentCommissionReport_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/fetchCommissionSplit'),
  )

export const getProperties: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.properties_request.type)),
    mergeMap(action => {
      return get(ENDPOINTS.PROPERTIES, state$, {}).pipe(
        pluck('response'),
        map(events.properties_success),
        catchRestError(action),
      )
    }),
    tag('reporting/epics/getProperties'),
  )

export const getOwnersAndAgents: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.quickSearch_request.type)),
    mergeMap(action =>
      get(PARTY_ENDPOINTS.QUICK_SEARCH, state$, action.payload).pipe(
        pluck('response'),
        map((results: TQuickSearchSuccess['results']) =>
          events.quickSearch_success({ results, params: action.payload }),
        ),
        catchRestError(action),
      ),
    ),
    tag('reporting/epics/getOwnersAndAgents'),
  )

export const propertyTaxStatement: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.propertyTaxStatement_request.type)),
    mergeMap(action => {
      const payload = action.payload
      const { start = '', finish = '', ownerId, ownerName } = payload
      return get(`${ENDPOINTS.PROPERTY_TAX_STATEMENT}/${ownerId}/pdf`, state$, {
        start_date: start,
        end_date: finish,
      }).pipe(
        pluck('response'),
        map((res: TBase64PdfResponse) => {
          downloadBase64(res.pdfBase64, 'application/pdf', `Property Tax Statement_${ownerName}_${start}_${finish}.pdf`)
          return events.propertyTaxStatement_success({})
        }),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/propertyTaxStatement'),
  )

export const ownerStatement: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.ownerStatement_request.type)),
    mergeMap(action => {
      const payload = action.payload
      const { start = '', finish = '', ownerId, ownerName } = payload
      return get(`${ENDPOINTS.OWNER_STATEMENT}/${ownerId}/pdf`, state$, {
        start_date: start,
        end_date: finish,
      }).pipe(
        pluck('response'),
        map((res: TBase64PdfResponse) => {
          downloadBase64(res.pdfBase64, 'application/pdf', `Owner Statement_${ownerName}_${start}_${finish}.pdf`)
          return events.ownerStatement_success({})
        }),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/ownerStatement'),
  )

export const franchisePerformance: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.franchisePerformance_request.type)),
    mergeMap(action => {
      const payload = action.payload
      const { start = '', finish = '', groupId } = payload
      return get(
        ENDPOINTS.FRANCHISE_PERFORMANCE + '/' + groupId,
        state$,
        { start, finish },
        { 'Content-Type': 'application/ms-excel' },
        { responseType: 'arraybuffer' },
      ).pipe(
        pluck('response'),
        downloadFileFromArrayBuffer('application/ms-excel', `Rawson Performance Report_${start}_${finish}.xlsx`),
        map(events.franchisePerformance_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/franchisePerformance'),
  )

export const royaltyReport: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.royaltyReport_request.type)),
    mergeMap(action => {
      const { start = '', finish = '', groupId } = action.payload
      return get(
        ENDPOINTS.ROYALTY_REPORT + '/' + groupId,
        state$,
        { start, finish },
        { 'Content-Type': 'application/ms-excel' },
        { responseType: 'arraybuffer' },
      ).pipe(
        pluck('response'),
        downloadFileFromArrayBuffer('application/ms-excel', `Fine Home Royalty Report_${start}_${finish}.xlsx`),
        map(events.royaltyReport_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/royaltyReport'),
  )

export const reportUsage: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.reportUsage_request.type)),
    mergeMap(action => {
      const payload = action.payload
      const { start = '', finish = '', dataType = 'excel', include_dashboard = false, source = 'auto' } = payload
      return get(
        ENDPOINTS.REPORT_USAGE,
        state$,
        { from: start, to: finish, format: dataType, include_dashboard, source },
        { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
        { responseType: 'arraybuffer' },
      ).pipe(
        pluck('response'),
        downloadFileFromArrayBuffer(
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          `Report Usage by Agency_${start}_${finish}.xlsx`,
        ),
        map(events.reportUsage_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/reportUsage'),
  )

export const activeAgencies: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.activeAgencies_request.type)),
    mergeMap(action => {
      return get(
        ENDPOINTS.ACTIVE_AGENCIES,
        state$,
        {},
        { 'Content-Type': 'application/ms-excel' },
        { responseType: 'arraybuffer' },
      ).pipe(
        pluck('response'),
        downloadFileFromArrayBuffer('application/ms-excel', `reOS Active Agencies.xlsx`),
        map(events.activeAgencies_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/activeAgencies'),
  )

export const activeLeases: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.activeLeases_request.type)),
    mergeMap(action => {
      const { start, finish } = action.payload
      return get(
        ENDPOINTS.ACTIVE_LEASES,
        state$,
        { start, finish },
        { 'Content-Type': 'application/ms-excel' },
        { responseType: 'arraybuffer' },
      ).pipe(
        pluck('response'),
        downloadFileFromArrayBuffer('application/ms-excel', `reOS Active Customers_${start}_${finish}.xlsx`),
        map(events.activeLeases_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/activeLeases'),
  )

export const availableFunds: ReportingEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.availableFunds_request.type)),
    mergeMap(action => {
      const currentAgencyId = userApiSelectors.getCurrentAgencyId(state$.value)
      const tradingOrEntity = agencyApiSelectors.getAgencyTradingOrCompanyName(state$.value)(currentAgencyId)
      const currentDate = new Date()
      return post(ENDPOINTS.AVAILABLE_FUNDS, state$, { filters: {} }, null, {}, { responseType: 'arraybuffer' }).pipe(
        pluck('response'),
        downloadFileFromArrayBuffer(
          'application/ms-excel',
          `Available Funds_${tradingOrEntity}_${format(currentDate, 'yyyy-MM-dd')}.xlsx`,
        ),
        map(events.availableFunds_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/availableFunds'),
  )

export const ficaCompliance: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.ficaCompliance_request.type)),
    mergeMap(action => {
      const currentAgencyId = userApiSelectors.getCurrentAgencyId(state$.value)
      const tradingOrEntity = agencyApiSelectors.getAgencyTradingOrCompanyName(state$.value)(currentAgencyId)
      const currentDate = new Date()
      return get(
        ENDPOINTS.FICA_COMPLIANCE,
        state$,
        {},
        { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
        { responseType: 'arraybuffer' },
      ).pipe(
        pluck('response'),
        downloadFileFromArrayBuffer(
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          `FICA Compliance_${tradingOrEntity}_${format(currentDate, 'yyyy-MM-dd')}.xlsx`,
        ),
        map(events.ficaCompliance_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/ficaCompliance'),
  )

export const suspenseAccountAllocated: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.suspenseAccountAllocated_request.type)),
    mergeMap(action => {
      const payload = action.payload
      const { start = '', finish = '' } = payload
      return get(
        ENDPOINTS.SUSPENSE_ACCOUNT_ALLOCATED,
        state$,
        { start_date: start, end_date: finish },
        { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
        { responseType: 'arraybuffer' },
      ).pipe(
        pluck('response'),
        downloadFileFromArrayBuffer(
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          `Suspense Account Allocated_${format(new Date(start), 'yyyy-MM-dd')}_${format(
            new Date(finish),
            'yyyy-MM-dd',
          )}.xlsx`,
        ),
        map(events.suspenseAccountAllocated_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/suspenseAccountAllocated'),
  )

export const suspenseAccountUnallocated: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.suspenseAccountUnallocated_request.type)),
    mergeMap(action => {
      const payload = action.payload
      const { start = '', finish = '' } = payload
      return get(
        ENDPOINTS.SUSPENSE_ACCOUNT_UNALLOCATED,
        state$,
        { start_date: start, end_date: finish },
        { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
        { responseType: 'arraybuffer' },
      ).pipe(
        pluck('response'),
        downloadFileFromArrayBuffer(
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          `Suspense Account Unallocated_${format(new Date(start), 'yyyy-MM-dd')}_${format(
            new Date(finish),
            'yyyy-MM-dd',
          )}.xlsx`,
        ),
        map(events.suspenseAccountUnallocated_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/suspenseAccountUnallocated'),
  )

export const suspenseAccountCsv = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.suspenseAccountCsv_request.type)),
    mergeMap(action => {
      const currentDate = new Date()
      return get(
        ENDPOINTS.SUSPENSE_ACCOUNT_CSV,
        state$,
        null,
        { 'Content-Type': 'text/csv' },
        { responseType: 'text' },
      ).pipe(
        pluck('response'),
        downloadFileFromArrayBuffer('text/csv', `Suspense Account_${format(currentDate, 'yyyy-MM-dd')}.csv`),
        map(events.suspenseAccountCsv_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/suspenseAccountCsv'),
  )

export const fetchSuspenseAccountStats: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.suspenseAccountStats_request.type)),
    switchMap(action =>
      get(ENDPOINTS.SUSPENSE_ACCOUNT_STATS, state$, {}).pipe(
        pluck('response'),
        map((res: TSuspenseAccountStats) =>
          events.suspenseAccountStats_success(transformers.transformSuspenseAccountStats(res)),
        ),
        catchRestError(action),
      ),
    ),
    tag('reportingApi/epics/fetchSuspenseAccountStats'),
  )

/**
 * The reducer has already merged the request's query into the list's, so the request is built
 * from state. Each list keeps only its latest request, so a fast run of page or sort changes
 * can't land out of order. An unpaged list fetches its remaining pages and combines them.
 */
export const fetchSuspenseAccountList: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.suspenseAccountList_request.type)),
    groupBy(action => action.payload.list),
    mergeMap(list$ =>
      list$.pipe(
        switchMap(action => {
          const { list } = action.payload
          const { query } = selectors.getSuspenseAccountList(state$.value)(list)
          const fetchPage = (page: number): Observable<TSuspenseListResponse> =>
            get(ENDPOINTS.SUSPENSE_ACCOUNT_LIST, state$, {
              list,
              ...transformers.transformSuspenseListQuery({ ...query, page }),
            }).pipe(pluck('response'))

          return fetchPage(query.page).pipe(
            mergeMap(first =>
              !SUSPENSE_LISTS_UNPAGED.includes(list) || first.totalPages <= 1
                ? of(first)
                : forkJoin(Array.from({ length: first.totalPages - 1 }, (_, i) => fetchPage(i + 2))).pipe(
                    map(rest => ({ ...first, items: first.items.concat(...rest.map(page => page.items)) })),
                  ),
            ),
            map((res: TSuspenseListResponse) =>
              events.suspenseAccountList_success({
                list,
                response: transformers.transformSuspenseListResponse(res),
              }),
            ),
            catchRestError(action),
          )
        }),
      ),
    ),
    tag('reportingApi/epics/fetchSuspenseAccountList'),
  )

export const fetchSuspenseReferenceLookup: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.suspenseReferenceLookup_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.SUSPENSE_ACCOUNT_REFERENCE, state$, action.payload).pipe(
        pluck('response'),
        map((res: { result?: TSuspenseReferenceLookup } | null) => events.suspenseReferenceLookup_success(res?.result)),
        catchRestError(action),
      ),
    ),
    tag('reportingApi/epics/fetchSuspenseReferenceLookup'),
  )

/**
 * An allocation moves a row between lists and changes the totals, so all three are refetched,
 * once the read side has had a moment to catch up. A run of allocations shares the one refetch.
 */
export const refreshSuspenseAccountAfterAllocation: Epic<
  ReportingEvent | WalletEvent,
  ReportingEvent,
  RootState,
  EpicDependencies
> = action$ =>
  action$.pipe(
    filter(isOfType(walletEvents.allocateSuspenseAccount_success.type)),
    debounceTime(SUSPENSE_REFRESH_DELAY_MS),
    mergeMap(() => [
      events.suspenseAccountStats_request(),
      events.suspenseAccountList_request({ list: 'unallocated' }),
      events.suspenseAccountList_request({ list: 'allocated' }),
    ]),
    tag('reportingApi/epics/refreshSuspenseAccountAfterAllocation'),
  )

export const walletNumbers: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.wallets_request.type)),
    mergeMap(action => {
      const currentAgencyId = userApiSelectors.getCurrentAgencyId(state$.value)
      const tradingOrEntity = agencyApiSelectors.getAgencyTradingOrCompanyName(state$.value)(currentAgencyId)
      const currentDate = new Date()
      return get(
        ENDPOINTS.WALLETS,
        state$,
        {},
        { 'Content-Type': 'application/ms-excel' },
        { responseType: 'arraybuffer' },
      ).pipe(
        pluck('response'),
        downloadFileFromArrayBuffer(
          'application/ms-excel',
          `Wallet Numbers_${tradingOrEntity}_${format(currentDate, 'yyyy-MM-dd')}.xlsx`,
        ),
        map(events.wallets_success),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/wallets'),
  )

export const downloadCreditNotePdf: ReportingEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.downloadCreditNotePdf_request.type)),
    mergeMap(action => {
      const payload = action.payload
      const { invoiceId, invoiceNumber } = payload
      return get(`${ENDPOINTS.DOWNLOAD_CREDIT_NOTE_PDF.replace(':invoiceId', invoiceId)}`, state$, {}).pipe(
        pluck('response'),
        map((res: TBase64PdfResponse) => {
          downloadBase64(res.pdfBase64, 'application/pdf', `Credit Note_INV-${invoiceNumber}.pdf`)
          return events.downloadCreditNotePdf_success({})
        }),
        catchRestError(action),
      )
    }),
    tag('reportingApi/epics/downloadCreditNotePdf'),
  )
